<template>
  <Teleport to="body">
    <dialog v-if="state" ref="dialogEl" class="confirm-dialog" @close="onClose" @keydown.esc.prevent="handle(false)">
      <h3 v-if="state.title" class="title">{{ state.title }}</h3>
      <p class="msg">{{ state.message }}</p>
      <div class="actions">
        <button class="btn btn-ghost" @click="handle(false)">
          {{ state.cancelText ?? 'Cancel' }}
        </button>
        <button ref="okBtn" class="btn"
                :class="state.danger ? 'btn-danger' : 'btn-primary'"
                @click="handle(true)">
          {{ state.okText ?? 'OK' }}
        </button>
      </div>
    </dialog>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { closeConfirm, confirmState } from '@/composables/confirm'

const state = computed(() => confirmState.value)
const dialogEl = ref<HTMLDialogElement | null>(null)
const okBtn = ref<HTMLButtonElement | null>(null)

watch(state, async (v) => {
  if (v) {
    await nextTick()
    if (dialogEl.value && !dialogEl.value.open) {
      dialogEl.value.showModal()
      // 기본 포커스 — 실수 클릭 방지를 위해 cancel 버튼에. danger는 더 보수적.
      // 여기선 primary(ok)에 포커스 해두되 Enter로 확인이 안전하도록.
      okBtn.value?.focus()
    }
  }
}, { flush: 'post' })

function handle(result: boolean) {
  const d = dialogEl.value
  closeConfirm(result)
  if (d?.open) d.close()
}

function onClose() {
  // 외부에서 dialog.close() 호출됐을 때 (ESC 등). state 남아있으면 false 처리.
  if (confirmState.value) closeConfirm(false)
}
</script>

<style scoped>
.confirm-dialog {
  border: none;
  border-radius: 12px;
  padding: 24px 28px;
  min-width: 320px;
  max-width: min(92vw, 520px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.28);
  background: var(--c-surface, #fff);
  color: var(--c-text, #0f172a);
  animation: cd-pop 0.15s ease-out;
}
.confirm-dialog::backdrop {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  animation: cd-fade 0.15s ease-out;
}
.title {
  margin: 0 0 12px 0;
  font-size: 1.05rem;
  font-weight: 600;
}
.msg {
  margin: 0 0 22px 0;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: keep-all;
  color: var(--c-text, #0f172a);
  font-size: 0.95rem;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.actions .btn {
  min-width: 80px;
}
@keyframes cd-pop {
  from { opacity: 0; transform: scale(0.96) translateY(-4px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes cd-fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}
</style>
